import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@features/ui';
import { LogoutIcon, PurchaseIcon } from '@features/ui/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@hooks/useAuth.ts';
import React from 'react';
import { useCheckoutStore } from '@src/stores/checkoutStore';
import { useNavigate } from 'react-router-dom';
import { TradingPlatForms } from '@src/types';
import { getAskAliseVisibilityConfig } from '@src/services/systemSettings';

import { NavItem } from '../navItem';
import { NavItemWithSub } from '../navItemWithSub';

import { sidebarData } from './userSidebarData';

export function UserSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation();
  const { logout, me } = useAuth();
  const { setPlatform, setIsPurchaseMode } = useCheckoutStore();
  const navigate = useNavigate();
  const [allowAskAlise, setAllowAskAlise] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;

    const loadSidebarConfig = async () => {
      try {
        const { data } = await getAskAliseVisibilityConfig();

        if (mounted) {
          setAllowAskAlise(data.allow_ask_alise);
        }
      } catch {
        if (mounted) {
          setAllowAskAlise(true);
        }
      }
    };

    loadSidebarConfig().then();

    return () => {
      mounted = false;
    };
  }, [me?.id]);

  const navItems = React.useMemo(() => {
    if (allowAskAlise) {
      return sidebarData.navItems;
    }

    return sidebarData.navItems.filter(
      (item) => item.title !== 'dashboardMenuItems.askAlise'
    );
  }, [allowAskAlise]);

  const handlePurchaseAccount = () => {
    setPlatform(me?.platform as TradingPlatForms);
    setIsPurchaseMode(true);
    navigate('/select-trading-account-type');
  };

  return (
    <Sidebar collapsible="icon" variant="floating" {...props}>
      <SidebarHeader className="mb-8 pl-7 pt-12">
        <img alt="logo" height={42} src="/assets/logo.svg" width={160} />
      </SidebarHeader>
      <SidebarContent>
        <SidebarMenu>
          {navItems.map((item) =>
            item.items ? (
              <NavItemWithSub key={item.title} item={item} />
            ) : (
              <NavItem key={item.title} item={item} />
            )
          )}
          <SidebarMenuItem className="my-8 px-2.5 w-full cursor-pointer">
            <div className="flex gap-x-5">
              <SidebarMenuButton
                asChild
                className="w-[240px]  h-[62px] gap-x-[18px] bg-gradient-to-b from-primary to-tertiary rounded-2xl text-[16px] justify-center"
                onClick={handlePurchaseAccount}
              >
                <div>
                  <PurchaseIcon />
                  <span>{t('dashboardMenuItems.purchaseAccount')}</span>
                </div>
              </SidebarMenuButton>
            </div>
          </SidebarMenuItem>
          {/* <NavItem
            item={{
              icon: SettingsIcon,
              title: 'dashboardMenuItems.settings',
              url: '/settings',
            }}
          /> */}
          <SidebarMenuItem>
            <div className="flex gap-x-5">
              <div className="w-1 rounded-tr-[4px] rounded-br-[4px]" />
              <SidebarMenuButton asChild className="w-48 h-[50px] gap-x-[18px]">
                <button className={'cursor-pointer'} onClick={logout}>
                  <LogoutIcon />
                  <span>{t('dashboardMenuItems.logout')}</span>
                </button>
              </SidebarMenuButton>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarContent>
    </Sidebar>
  );
}
